import { RadioField, RadioInput, Accordion, AccordionPanel } from '@flos/react-ui';
import React from 'react';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { SummaryRowTemplate } from '../../helpers/templates';
import useAppState from '../../state/appState';
import {
    AutomaticConsent as AutomaticConsentType,
    FormConfig,
    FormValues
} from '@topdanmark-onlinesolutions/claim-form-types';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { ElementValueHuman } from '../../types/element-values';
import { getValue } from '../values/Get';

const AutomaticConsent = ({ values, caption, elementId }: AutomaticConsentType) => {
    const formValues = useAppState((state) => state.formValues);
    const formConfig = useAppState((state) => state.formConfig);
    const setFormValue = useAppState((state) => state.setFormValue);
    const [, /*selectedIndex*/ setSelectedIndex] = React.useState<number | null>(null);
    const [fieldHasChangedAtLeastOnce, setFieldHasChangedAtLeastOnce] = React.useState(false);
    //const appConfig = useContext(ConfigContext);

    const valueObj = AutomaticConsent.getValueOrDefault(formValues, elementId);
    const isValid = AutomaticConsent.isValidField(formConfig, formValues, elementId, fieldHasChangedAtLeastOnce);

    return (
        <>
            <p>
                Automatiske afgørelser gør typisk sagsbehandlingstiden væsentlig kortere, så du allerede inden for få
                dage modtager svar på din anmeldelse og en eventuel erstatning på din NemKonto.
            </p>
            <Accordion onChange={setSelectedIndex}>
                <AccordionPanel title="Sådan foregår automatisk skadebehandling">
                    <div className="Box">
                        <p>
                            Når vi behandler erstatningsspørgsmål automatisk, sammenlignes din skadeanmeldelse med
                            vilkårene og med lignende anmeldelser. Har du fx oplyst, at du har brækket en arm, kan
                            anmeldelsen hurtigt afgøres, fordi der står i vilkårene, hvordan forsikringen dækker et
                            sådant brud. Du har dog altid har ret til at bede om, at en skadebehandler ser på din sag
                            efterfølgende.
                        </p>
                    </div>
                </AccordionPanel>
                <AccordionPanel title="Hvornår kommer der en skadebehandler ind over?">
                    <div className="Box">
                        <p>
                            Hvis resultatet af den automatiske behandling er, at din skade ikke er dækket, sendes
                            anmeldelsen videre til en skadebehandler, som så vurderer din anmeldelse.
                        </p>
                    </div>
                </AccordionPanel>
            </Accordion>
            <br />
            <p>
                Vi anvender dit navn, dit cpr.nr., dine skade- og helbredsoplysninger og øvrige oplysninger fra din
                anmeldelse for at kunne træffe afgørelsen. Du kan nå at trække dit samtykke tilbage ved at ringe til os,
                inden afgørelsen er truffet.
            </p>

            <RadioField
                isValid={isValid}
                label={caption}
                id={elementId}
                key={elementId}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                    const value = evt.target.value;
                    const label = values.find((item) => item.value === value)?.caption;
                    setFormValue(elementId, { label, value });
                    setFieldHasChangedAtLeastOnce(true);
                }}
                value={valueObj.value}
            >
                {values.map((option, key) => (
                    <RadioInput key={key} label={option.caption} value={option.value} />
                ))}
            </RadioField>
        </>
    );
};

AutomaticConsent.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const valueObject = getValue(formConfig, formValues, elementId);

    if (valueObject && valueObject.label !== '') {
        return true;
    }
    return false;
};

AutomaticConsent.getValue = (formValues: FormValues, elementId: string) => {
    return formValues[elementId];
};

AutomaticConsent.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = AutomaticConsent.getValue(formValues, elementId);
    if (value === undefined) {
        return { label: '', value: '' };
    }
    return value;
};

AutomaticConsent.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasChangedAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasChangedAtLeastOnce) {
        return undefined;
    }

    const value = AutomaticConsent.getValue(formValues, elementId);

    if (value === '' || value === undefined) {
        return undefined;
    }

    const isValid = AutomaticConsent.isValid(formConfig, formValues, elementId);

    return isValid;
};

AutomaticConsent.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as AutomaticConsentType;
    const { summaryCaption, values } = element;

    const value = getValue(formConfig, formValues, elementId);

    if (value === undefined) {
        return <></>;
    }

    const option = values.find((item) => item.value === value.value);

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={option?.caption} />;
};

AutomaticConsent.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    label: string
): ClaimFormSubmitFieldValue => {
    const valueObj = AutomaticConsent.getValue(formValues, elementId);
    const value = valueObj.value;
    const labelX = valueObj.label;

    const valueId = { string: value };

    return getClaimFormSubmitValue('automaticConsent', elementId, label, labelX, valueId);
};

AutomaticConsent.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const valueObj = AutomaticConsent.getValue(formValues, elementId);
    const valueStr = JSON.stringify(valueObj);

    return valueStr;
};

export default AutomaticConsent;
